import { z } from 'zod';
import { idSchema, localRefSchema, nodeKeySchema } from './refs.js';

export const THEMES = ['system', 'light', 'dark'] as const;
export const MOTION_PREFERENCES = ['system', 'reduced', 'full'] as const;
/** Ambient backdrop and glass family. `auto` follows the workspace clock: Aurora by day, Deep at night. */
export const ATMOSPHERES = ['auto', 'aurora', 'fog', 'deep'] as const;
export const WIDGET_SIZES = ['s', 'm', 'l', 'w'] as const;
export const homeWidgetSchema = z.object({ id: z.string().regex(/^[a-z0-9-]+\.[a-z0-9.-]+$/).max(80), size: z.enum(WIDGET_SIZES) });
export type HomeWidget = z.infer<typeof homeWidgetSchema>;

export const meSchema = z.object({
  user: z.object({ id: z.string(), displayName: z.string(), authIssuer: z.string() }),
  person: z.object({ id: z.string(), name: z.string() }),
  workspace: z.object({
    id: z.string(),
    name: z.string(),
    timezone: z.string(),
    locale: z.string(),
    isDemo: z.boolean(),
  }),
  enabledModules: z.array(z.string()),
  csrfToken: z.string(),
  authMode: z.enum(['oidc', 'development']),
});
export type Me = z.infer<typeof meSchema>;

function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat('en', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

export const updateProfileSchema = z.object({
  displayName: z.string().trim().min(1).max(80).optional(),
  workspaceName: z.string().trim().min(1).max(80).optional(),
  timezone: z.string().refine(isValidTimeZone, 'Unknown IANA time zone').optional(),
  locale: z.string().regex(/^[a-z]{2,3}(-[A-Z]{2})?$/).optional(),
});

export const pinSchema = z.object({ ref: localRefSchema, label: z.string().max(120) });

export const preferencesSchema = z.object({
  theme: z.enum(THEMES),
  motionPreference: z.enum(MOTION_PREFERENCES),
  pinnedRefs: z.array(pinSchema).max(12),
  homeLayout: z.enum(['composed', 'compact']),
  atmosphere: z.enum(ATMOSPHERES),
  /** null = not arranged yet: the desktop proposes a layout from enabled modules. */
  homeWidgets: z.array(homeWidgetSchema).max(24).nullable(),
  dockModules: z.array(z.string().max(40)).max(12).nullable(),
  revision: z.number().int(),
});
export type Preferences = z.infer<typeof preferencesSchema>;

export const updatePreferencesSchema = z.object({
  theme: z.enum(THEMES).optional(),
  motionPreference: z.enum(MOTION_PREFERENCES).optional(),
  pinnedRefs: z.array(pinSchema).max(12).optional(),
  homeLayout: z.enum(['composed', 'compact']).optional(),
  atmosphere: z.enum(ATMOSPHERES).optional(),
  homeWidgets: z.array(homeWidgetSchema).max(24).nullable().optional(),
  dockModules: z.array(z.string().max(40)).max(12).nullable().optional(),
  expectedRevision: z.number().int().min(0),
});

export const moduleStatusSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  enabled: z.boolean(),
  version: z.string(),
  requiresModules: z.array(z.string()),
  devOnly: z.boolean(),
});
export type ModuleStatus = z.infer<typeof moduleStatusSchema>;
export const toggleModuleSchema = z.object({ enabled: z.boolean() });

export const personSchema = z.object({
  id: z.string(),
  name: z.string(),
  externalIds: z.array(z.object({ provider: z.string(), id: z.string() })),
  revision: z.number().int(),
});
export type Person = z.infer<typeof personSchema>;

/** people.resolve@1: pick an existing person by id, by provider id, or explicitly create one. */
export const personResolveInputSchema = z.union([
  z.object({ personId: idSchema }),
  z.object({
    name: z.string().trim().min(1).max(160),
    externalId: z.object({ provider: z.literal('openlibrary'), id: z.string().regex(/^OL\d+A$/) }).optional(),
    create: z.literal(true),
  }),
]);
export type PersonResolveInput = z.infer<typeof personResolveInputSchema>;

export const entityPreviewSchema = z.object({
  key: z.string(),
  ref: localRefSchema,
  typeKey: z.string(),
  typeLabel: z.string(),
  title: z.string(),
  summary: z.string().nullable(),
  href: z.string(),
  preview: z.record(z.string(), z.union([z.string(), z.number(), z.null()])).nullable(),
  updatedAt: z.string(),
});
export type EntityPreview = z.infer<typeof entityPreviewSchema>;

export const searchQuerySchema = z.object({
  q: z.string().trim().min(1).max(120),
  cursor: z.string().max(400).optional(),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  types: z.string().max(400).optional(),
});

export const relationOriginSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('manual'), creatorUserId: z.string() }),
  z.object({
    kind: z.literal('projection'),
    moduleId: z.string(),
    sourceKey: z.string(),
    projectorVersion: z.number().int(),
  }),
]);

export const relationSchema = z.object({
  id: z.string(),
  from: z.string(),
  to: z.string(),
  predicate: z.string(),
  predicateLabel: z.string(),
  directed: z.boolean(),
  origin: relationOriginSchema,
  evidence: z.object({ sourceKey: z.string().nullable(), explanation: z.string() }),
  createdAt: z.string(),
  revision: z.number().int(),
});
export type RelationDto = z.infer<typeof relationSchema>;

export const createRelationSchema = z.object({
  from: nodeKeySchema,
  to: nodeKeySchema,
  predicate: z.string().max(80),
  explanation: z.string().trim().max(500).default(''),
});
export type CreateRelationInput = z.input<typeof createRelationSchema>;

export const activitySchema = z.object({
  id: z.string(),
  verb: z.string(),
  entity: entityPreviewSchema,
  occurredAt: z.string(),
});
export type Activity = z.infer<typeof activitySchema>;

/** Home is composed from typed module contributions. Each module parses its own `data`. */
export const homeSectionSchema = z.object({
  moduleId: z.string(),
  kind: z.string(),
  data: z.unknown(),
});
export type HomeSection = z.infer<typeof homeSectionSchema>;
export const homeSchema = z.object({
  sections: z.array(homeSectionSchema),
  activity: z.array(activitySchema),
  pins: z.array(z.object({ key: z.string(), label: z.string(), preview: entityPreviewSchema.nullable() })),
  pendingProjections: z.number().int(),
});
export type Home = z.infer<typeof homeSchema>;

export const integrationStatusSchema = z.object({
  providerId: z.string(),
  name: z.string(),
  capability: z.string(),
  mode: z.enum(['live', 'fixture', 'unconfigured', 'disabled']),
  lastSuccessAt: z.string().nullable(),
  lastErrorCode: z.string().nullable(),
  lastErrorAt: z.string().nullable(),
  attribution: z.string(),
  explanation: z.string(),
});
export type IntegrationStatus = z.infer<typeof integrationStatusSchema>;

export const exportJobSchema = z.object({
  id: z.string(),
  status: z.enum(['queued', 'running', 'succeeded', 'failed']),
  createdAt: z.string(),
  completedAt: z.string().nullable(),
  bytes: z.number().int().nullable(),
  sha256: z.string().nullable(),
  errorCode: z.string().nullable(),
  downloadHref: z.string().nullable(),
});
export type ExportJob = z.infer<typeof exportJobSchema>;

export const assetSchema = z.object({
  id: z.string(),
  mime: z.string(),
  bytes: z.number().int(),
  sha256: z.string(),
  originalName: z.string(),
  contentHref: z.string(),
  createdAt: z.string(),
});
export type AssetDto = z.infer<typeof assetSchema>;
export const ASSET_LIMITS = { pdfMaxBytes: 60 * 1024 * 1024 } as const;

export const diagnosticsSchema = z.object({
  outbox: z.object({ pending: z.number(), failed: z.number(), blocked: z.number() }),
  failedEvents: z.array(
    z.object({ id: z.string(), type: z.string(), attempts: z.number(), lastError: z.string().nullable() }),
  ),
  jobs: z.object({ queued: z.number(), failed: z.number() }),
});
